/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { Close as CloseIcon, Download as DownloadIcon, ExpandMore as ExpandMoreIcon } from 'components/icons';
import { cn } from 'utils/cn';
import { Progress as ProgressPrimitive } from '@base-ui/react/progress';
import IconButton from 'components/@extended/IconButton';
import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import CircularLoader from 'components/CircularLoader';
import { Button } from 'components/ui/button';
import { ProgressIndicator, ProgressTrack } from 'components/ui/progress';
import TimeSinceDate from 'components/util/TimeSinceDate';
import { generateCSV } from 'components/xlsx/xlsx';
import { generateStyledXLSX } from 'components/xlsx/xlsxStyled';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { dispatch, useSelector } from 'store';
import {
    useDeleteCompletedTasksMutation,
    useDeleteExportTaskMutation,
    useGetExportTaskQuery,
    useGetExportTasksQuery,
} from 'store/api/exports';
import {
    clearCompletedExports,
    hideRibbon,
    removeExport,
    selectIsRibbonOnScreen,
    setExports,
    toggleRibbon,
    updateExport,
} from 'store/reducers/exports';
import {
    ActiveExport,
    EXPORT_CONFIG,
    exportFormatOf,
    ExportResultData,
    ExportStatus,
    mapTaskListResponseToActiveExport,
} from 'types/exports';
import { FeatureFlag } from 'types/features';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { captureListExportEnd } from 'utils/listAnalytics';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

const ICON_BUTTON_CLASS = 'text-fg-secondary hover:text-fg';

/**
 * For another tab on the bottom edge while the ribbon shows: 12px clear of the ribbon's left side, and no wider
 * than the room left of it. It follows the ribbon's own right offset, which clears the AI side dock, and `w-[480px]`.
 */
export const LEFT_OF_EXPORT_RIBBON_CLASS =
    'sm:right-[calc(480px+2.25rem+var(--ai-side-dock-width,0px))] sm:max-w-[calc(100vw-480px-3.75rem-var(--ai-side-dock-width,0px))]';

/** Whether the ribbon is on screen: there are exports, and the user has not closed it. */
export function useIsExportRibbonShown(): boolean {
    return useSelector(selectIsRibbonOnScreen);
}

export default function ExportRibbon() {
    const { activeExports, isRibbonMinimized } = useSelector((state) => state.exports);
    const isShown = useIsExportRibbonShown();

    // Fetch tasks from server to restore state after page refresh
    const hasExports = Object.keys(activeExports).length > 0;
    const { data: tasksFromServer } = useGetExportTasksQuery(undefined, {
        skip: hasExports,
    });

    // Restore exports from server when component mounts and no exports in Redux
    useEffect(() => {
        if (tasksFromServer && !hasExports) {
            const activeExportsList = tasksFromServer
                .map(mapTaskListResponseToActiveExport)
                .filter((exp): exp is ActiveExport => exp !== null);

            if (activeExportsList.length > 0) {
                dispatch(setExports(activeExportsList));
            }
        }
    }, [tasksFromServer, hasExports]);

    const handleToggle = () => {
        dispatch(toggleRibbon());
    };

    const handleClose = () => {
        dispatch(hideRibbon());
    };

    const [deleteCompletedTasks] = useDeleteCompletedTasksMutation();

    const handleClearCompleted = async () => {
        try {
            await deleteCompletedTasks().unwrap();
            dispatch(clearCompletedExports());
        } catch (error) {
            console.error('Failed to clear completed tasks:', error);
            // Still clear from UI even if backend fails
            dispatch(clearCompletedExports());
        }
    };

    const exportCount = Object.keys(activeExports).length;

    if (!isShown) {
        return null;
    }

    return (
        // One step under the floating AI launcher, which sits over it where they overlap; the add button sits under it.
        <div className="bg-paper fixed right-[calc(1.5rem+var(--ai-side-dock-width,0px))] bottom-0 z-[calc(var(--z-floating-ai-launcher)-1)] flex max-h-[60dvh] w-[480px] flex-col rounded-t-lg shadow-xl">
            {/* Header */}
            <div className="border-divider bg-grey-50 flex items-center justify-between border-b px-4 py-3">
                <h6 className="text-h6 font-semibold">
                    <FormattedMessage id="exports" /> ({exportCount})
                </h6>
                <div className="flex flex-row gap-2">
                    <IconButton
                        size="small"
                        aria-label={t(isRibbonMinimized ? 'expand' : 'collapse')}
                        onClick={handleToggle}
                        className={ICON_BUTTON_CLASS}
                    >
                        <ExpandMoreIcon
                            className={cn(
                                'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                isRibbonMinimized && '-scale-y-100'
                            )}
                        />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={t('close')}
                        onClick={handleClose}
                        className={ICON_BUTTON_CLASS}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </div>

            {/* Content — hidden rather than unmounted while minimized, so the items keep polling. */}
            <div hidden={isRibbonMinimized} className="max-h-[calc(60dvh-60px)] overflow-y-auto p-2">
                <div className="flex flex-col gap-2">
                    {Object.values(activeExports)
                        .sort((a, b) => b.startedAt - a.startedAt)
                        .map((exp) => (
                            <ExportItem key={exp.taskId} export={exp} />
                        ))}
                </div>
                {Object.values(activeExports).some((exp) => exp.status === 'COMPLETED' || exp.status === 'FAILED') && (
                    <div className="p-2 text-center">
                        <Button
                            {...buttonStyle({ variant: 'text' })}
                            onClick={() => {
                                void handleClearCompleted();
                            }}
                        >
                            <FormattedMessage id="clear-completed" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

interface ExportItemProps {
    export: ActiveExport;
}

function ExportItem({ export: exportData }: ExportItemProps) {
    const { taskId, fileName, status, errorMessage, startedAt, entityType, surface } = exportData;
    const { isTwoDecimalDisplayEnabled } = useTimecardPrecision();
    const isBilledTimeControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_BILLED_TIME_CONTROL);
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const isLastHcsMeetingEnabled = useIsFeatureEnabled(FeatureFlag.LAST_HCS_MEETING);
    const isRankingsRefereeEnabled = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);

    // Get config for this entity type (mapper and classifier codes)
    const config = EXPORT_CONFIG[entityType];

    // Fetch classifiers needed for the mapper
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: config.classifierCodes,
    });

    // Poll export status every 500ms if not in terminal state
    const shouldPoll = status === 'QUEUED' || status === 'PROCESSING';
    const { data: taskResponse, isFetching } = useGetExportTaskQuery(taskId, {
        pollingInterval: shouldPoll ? 500 : 0,
    });

    const [deleteExportTask] = useDeleteExportTaskMutation();

    // Update local state when task status changes
    useEffect(() => {
        if (taskResponse && taskResponse.status !== status) {
            dispatch(
                updateExport({
                    taskId,
                    status: taskResponse.status,
                    completedAt: taskResponse.completedAt,
                    errorMessage: taskResponse.errorMessage,
                    totalRecords: taskResponse.result
                        ? (JSON.parse(taskResponse.result) as ExportResultData).metadata.totalRecords
                        : undefined,
                })
            );

            if (taskResponse.status === 'COMPLETED') {
                successAlert(`Export “${fileName}” completed successfully`);
            } else if (taskResponse.status === 'FAILED') {
                errorAlert(`Export “${fileName}” failed: ${taskResponse.errorMessage || 'Unknown error'}`);
            }
        }
    }, [taskResponse, status, taskId, fileName]);

    // On the status, not its change: an export a reload restores already ended is reported too.
    useEffect(() => captureListExportEnd({ taskId, status, surface, fileName }), [taskId, status, surface, fileName]);

    const handleDownload = async () => {
        if (!taskResponse?.result) {
            errorAlert('No export data available');
            return;
        }

        try {
            const exportResult = JSON.parse(taskResponse.result) as ExportResultData;

            // Apply mapper to transform raw data to export format with translated headers
            const mappedData = config.mapper(exportResult.data, classifiers, {
                isTwoDecimalEnabled: isTwoDecimalDisplayEnabled,
                isBilledTimeControlEnabled,
                isAdministratorFieldEnabled,
                isLastHcsMeetingEnabled,
                isRankingsRefereeEnabled,
            });

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            const extension = exportFormatOf(fileName);
            const downloadFileName = fileName.replace(/\.(csv|xlsx)$/, `_${timestamp}.${extension}`);

            if (extension === 'csv') {
                generateCSV(mappedData, downloadFileName, config.csvHeaders);
            } else {
                await generateStyledXLSX(mappedData, downloadFileName, config.styledXLSX?.());
            }
            successAlert(`Downloaded ${downloadFileName}`);

            // Delete task from cache after successful download
            try {
                await deleteExportTask(taskId).unwrap();
                dispatch(removeExport(taskId));
            } catch (deleteError) {
                console.error('Failed to delete export task:', deleteError);
                // Don't show error to user - download succeeded, cleanup failure is not critical
            }
        } catch (error) {
            errorAlert('Failed to generate export file');
            console.error('Export generation error:', error);
        }
    };

    const getStatusChipType = (status: ExportStatus) => {
        switch (status) {
            case 'COMPLETED':
                return 'success';
            case 'FAILED':
                return 'error';
            case 'PROCESSING':
                return 'info';
            case 'QUEUED':
            default:
                return 'neutral';
        }
    };

    return (
        <div className="border-divider bg-paper rounded border p-3">
            <div className="flex flex-col gap-2">
                {/* File name and status */}
                <div className="flex items-center justify-between">
                    <p className="text-body2 flex-1 overflow-hidden font-semibold text-ellipsis">{fileName}</p>
                    <StatusChip type={getStatusChipType(status)} label={status} size="small" />
                </div>

                {/* Created time */}
                <TimeSinceDate date={new Date(startedAt)} />

                {/* Progress bar for processing */}
                {status === 'PROCESSING' && (
                    <ProgressPrimitive.Root value={null}>
                        <ProgressTrack className="bg-grey-200 h-1 rounded-sm">
                            <ProgressIndicator className="loading-bar-sweep w-full rounded-sm transition-none" />
                        </ProgressTrack>
                    </ProgressPrimitive.Root>
                )}

                {/* Error message */}
                {errorMessage && <span className="text-caption text-error">{errorMessage}</span>}

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    {status === 'COMPLETED' && (
                        <Button
                            {...buttonStyle({ variant: 'outlined' })}
                            onClick={() => {
                                void handleDownload();
                            }}
                            disabled={isFetching || isLoadingClassifiers}
                        >
                            <span className={ICON_SLOT.small}>
                                {isFetching || isLoadingClassifiers ? (
                                    <CircularLoader className="static size-auto [&>span]:size-4 [&>span]:border-2" />
                                ) : (
                                    <DownloadIcon />
                                )}
                            </span>
                            <FormattedMessage id="download" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
